import { yupResolver } from '@hookform/resolvers/yup';
import { DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { useAssortmentTFunction } from '@retail/monitoring-assortment/i18n';
import { NewAssortmentForm, newAssortmentSchema } from '../newAssortmentForm';
import { NewAssortment } from '../ImportAssortment';
import { BodyLoader, TextInput } from '@retail/components';
import { useDisclosure } from '@shared/hooks';
import { CheckIcon } from '@shared/custom-icons';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (newAssortment: NewAssortment) => void;
  navigateToAssortment?: () => void;
  isLoading?: boolean;
  hideAddItemsLater?: boolean;
}

export const CreateMonitoringAssortmentDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  navigateToAssortment,
  isLoading,
  hideAddItemsLater,
}) => {
  const t = useAssortmentTFunction();
  const { handleSubmit, reset, getValues, control } = useForm<NewAssortmentForm>({
    mode: 'onSubmit',
    resolver: yupResolver(newAssortmentSchema),
    shouldUnregister: false,
  });
  const { isOpen: isCreated, onOpen: onIsCreated, onClose: cancelCreate } = useDisclosure();

  const handleOnClose = () => {
    cancelCreate();
    onClose();
    reset();
  };

  const onSubmitForm = handleSubmit(({ title }) => {
    onSubmit({ title });
    onIsCreated();
  });
  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      maxWidth="sm"
      fullWidth
      sx={{ '& .MuiPaper-root': { maxHeight: 260, minHeight: 260 } }}
    >
      <DialogTitle>{t('assortment.admin.create.title')}</DialogTitle>

      {isLoading ? (
        <Stack alignItems="center">
          <BodyLoader />
        </Stack>
      ) : isCreated ? (
        <>
          <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckIcon />
            <Typography variant="body1">{`Sortimentet "${
              getValues().title
            }" er opprettet med valgt vare`}</Typography>
          </DialogContent>
          <DialogActions>
            <TextButton size="small" onClick={handleOnClose} disabled={isLoading}>
              {t('assortment.common.close')}
            </TextButton>
            {navigateToAssortment && (
              <ContainedButton size="small" onClick={navigateToAssortment} loading={!!isLoading}>
                {t('assortment.goToAssortment')}
              </ContainedButton>
            )}
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <TextInput
              control={control}
              name="title"
              label={t('assortment.assortmentName')}
              placeholder={t('assortment.admin.create.form.title.label')}
              fullWidth
              required
            />
            {!hideAddItemsLater && (
              <Typography variant="body2">
                {t('assortment.admin.create.empty.description')}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <TextButton size="small" onClick={handleOnClose} disabled={isLoading}>
              {t('assortment.common.cancel')}
            </TextButton>
            <ContainedButton size="small" onClick={onSubmitForm} loading={!!isLoading}>
              {!hideAddItemsLater
                ? t('assortment.admin.create.empty.save')
                : t('assortment.admin.create.assortmentWithItemsPlural')}
            </ContainedButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
